/*
 * Segmented control with status dots (Figma Popup 119:45194 "tabs").
 * Group: --surface-page fill, padding 2px, radius 8, --shadow-segmented, fills its row.
 * Item: height 32, padding 2px 8px, gap 6, radius 6; selected fill --surface-default.
 * Dot 6px, radius 12. Label 550 14/20 --text-default.
 * Narrow screens (not in Figma): options that don't fit scroll sideways inside the group (no
 * scrollbar drawn), the selected one scrolled into view; on a touch screen the options are
 * --size-touch-target (40px) high.
 */
import { useLayoutEffect, useRef } from 'react'
import { cx } from './cx'
import { revealInStrip } from './StatusTabs'

export interface SegmentedOption<V extends string> {
    value: V
    label: string
    /** Dot colour, a CSS colour or var(). */
    dot?: string
}

export function SegmentedControl<V extends string>({
    options,
    value,
    onChange,
    ariaLabel,
}: {
    options: SegmentedOption<V>[]
    value: V
    onChange: (value: V) => void
    ariaLabel: string
}) {
    const strip = useRef<HTMLDivElement>(null)
    const selectedOption = useRef<HTMLButtonElement>(null)
    useLayoutEffect(() => revealInStrip(strip.current, selectedOption.current), [value])
    return (
        <div
            ref={strip}
            role="radiogroup"
            aria-label={ariaLabel}
            className="scrollbar-none relative flex max-w-full min-w-px flex-1 items-start overflow-x-auto overflow-y-clip rounded-[8px] bg-[var(--surface-page)] p-[2px] shadow-[var(--shadow-segmented)]"
        >
            {options.map((option) => {
                const selected = option.value === value
                return (
                    <button
                        key={option.value}
                        ref={selected ? selectedOption : undefined}
                        type="button"
                        role="radio"
                        aria-checked={selected}
                        onClick={() => onChange(option.value)}
                        className={cx(
                            'flex h-[32px] shrink-0 cursor-pointer items-center justify-center gap-[6px] rounded-[6px] px-[8px] py-[2px]',
                            'focus-visible:shadow-[var(--focus-m)] focus-visible:outline-none pointer-coarse:h-[var(--size-touch-target)]',
                            selected && 'bg-[var(--surface-default)]',
                        )}
                    >
                        {option.dot && <span aria-hidden className="size-[6px] shrink-0 rounded-[12px]" style={{ background: option.dot }} />}
                        <span className="whitespace-nowrap text-[14px] leading-[20px] font-[550] text-[var(--text-default)]">{option.label}</span>
                    </button>
                )
            })}
        </div>
    )
}
